import React from 'react';

import type { AskQuestion, AskUserQuestionResult, PendingAskUserQuestion } from './types';

let pending: PendingAskUserQuestion | null = null;
const listeners = new Set<() => void>();

/** Eval-only responder: given the pending question(s), returns how to resolve them. */
export type AskUserQuestionEvalResolver = (questions: AskQuestion[]) => AskUserQuestionResult;

let evalResolver: AskUserQuestionEvalResolver | null = null;

/**
 * Eval-only, keyed by the asking agent's conversationId. Eval cases run concurrently in
 * one JS realm (Vitest browser mode), so a single global resolver is stomped by whichever
 * case set it last — a case can then get another case's answer. Each Conversation has a
 * unique id (Conversation.getId()) that reaches the ask() call site via the tool context,
 * so a per-conversation resolver keeps concurrent cases isolated without any locking. Empty
 * in production — no eval resolver is ever registered there.
 */
const evalResolversByConversationId = new Map<string, AskUserQuestionEvalResolver>();

/**
 * Eval-only: with no human to answer, `ask` would block the tool loop forever.
 * Install a resolver and every request resolves immediately from it — dismiss (the
 * usual default) or a chosen answer (to drive a flow through a prompt/picker). A
 * dismissal is a normal outcome: the agent is told to proceed on its best judgement
 * and the turn continues. Null in production — the real flow always waits for the
 * user. This global is the fallback used when a request carries no conversationId or
 * has no keyed resolver (e.g. the default-dismiss from setupBackendServices).
 */
export function setAskUserQuestionEvalResolver(resolver: AskUserQuestionEvalResolver | null): void {
    evalResolver = resolver;
}

/**
 * Eval-only: register a resolver for a specific conversation. Concurrency-safe — a
 * concurrent case with a different conversationId is unaffected. Clear it with
 * clearEvalResolver once the turn is done.
 */
export function registerEvalResolver(
    conversationId: string,
    resolver: AskUserQuestionEvalResolver,
): void {
    evalResolversByConversationId.set(conversationId, resolver);
}

export function clearEvalResolver(conversationId: string): void {
    evalResolversByConversationId.delete(conversationId);
}

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Registers a question request and resolves once the user answers or dismisses
 * it. If a request is already pending, the new one resolves immediately as
 * dismissed so the conversation loop can never deadlock on overlapping asks.
 * Cancelling `abortSignal` dismisses this request only, never one another asker left showing.
 */
export function ask(
    questions: AskQuestion[],
    conversationId?: string,
    intro?: string,
    abortSignal?: AbortSignal,
): Promise<AskUserQuestionResult> {
    const keyedResolver = conversationId
        ? evalResolversByConversationId.get(conversationId)
        : undefined;
    if (keyedResolver) {
        return Promise.resolve(keyedResolver(questions));
    }
    if (evalResolver) {
        return Promise.resolve(evalResolver(questions));
    }
    if (pending || abortSignal?.aborted) {
        return Promise.resolve({ status: 'dismissed' });
    }
    return new Promise<AskUserQuestionResult>((resolve) => {
        const request: PendingAskUserQuestion = {
            questions,
            intro,
            resolve: (result) => {
                abortSignal?.removeEventListener('abort', dismissThisRequest);
                resolve(result);
            },
        };
        function dismissThisRequest() {
            if (pending === request) {
                resolvePending({ status: 'dismissed' });
            }
        }
        abortSignal?.addEventListener('abort', dismissThisRequest, { once: true });
        pending = request;
        notify();
    });
}

export function getPending(): PendingAskUserQuestion | null {
    return pending;
}

export function resolvePending(result: AskUserQuestionResult): void {
    if (!pending) {
        return;
    }
    const { resolve } = pending;
    pending = null;
    notify();
    resolve(result);
}

export function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useAskUserQuestion(): PendingAskUserQuestion | null {
    return React.useSyncExternalStore(subscribe, getPending, getPending);
}

export function useHasPendingQuestion(): boolean {
    return useAskUserQuestion() != null;
}
